import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar,
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const tt = { borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' };

const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return `rgba(${r},${g},${b},${a})`; };
const fmtCurrency = (v) => { const n = parseFloat(v || 0); if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M'; if (n >= 1e3) return '$' + Math.round(n / 1e3) + 'K'; return '$' + n.toLocaleString(); };

function KPI({ label, value, iconName, color, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: hexToRgba(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={22} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function DashFunding() {
  const token = localStorage.getItem('doorly_admin_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/dashboard/funding-data`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <AdminLayout><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#9ca3af', fontSize: '14px' }}>Loading...</div></AdminLayout>;

  const fac = data?.facility || {};
  const port = data?.portfolio || {};
  const rates = data?.rates || {};
  const monthly = data?.monthly || [];

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Funding</h1>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Capital utilization and lending economics</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          <KPI label="Total Facility" value={fmtCurrency(fac.total)} iconName="currency" color="#3a6ad9" />
          <KPI label="Deployed Capital" value={fmtCurrency(fac.deployed)} iconName="chart" color="#3a6ad9" />
          <KPI label="Utilization" value={`${fac.utilization || 0}%`} iconName="chart" color={fac.utilization > 85 ? '#ff082f' : '#ffdd86'} />
          <KPI label="Portfolio Yield" value={`${rates.portfolio_yield || 0}%`} iconName="arrow" color="#acf44e" />
          <KPI label="Cost of Capital" value={`${rates.cost_of_capital || 0}%`} iconName="currency" color="#ff082f" />
          <KPI label="Net Spread" value={`${rates.net_spread || 0}%`} iconName="check-circle" color="#3a6ad9" />
        </div>

        {/* Utilization bar */}
        <div style={{ ...card, marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Credit Facility Utilization</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: fac.utilization > 85 ? '#ff082f' : '#111827' }}>{fac.utilization || 0}%</span>
          </div>
          <div style={{ height: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(fac.utilization || 0, 100)}%`, height: '100%', backgroundColor: fac.utilization > 85 ? '#ff082f' : fac.utilization > 70 ? '#ffdd86' : '#acf44e', borderRadius: '6px', transition: 'width 0.5s' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px', color: '#9ca3af' }}>
            <span>Deployed: {fmtCurrency(fac.deployed)}</span>
            <span>Available: {fmtCurrency(fac.available)}</span>
          </div>
        </div>

        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
          {/* Portfolio Stats */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Portfolio</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Active Loans', value: port.active_loans || 0, color: '#3a6ad9' },
                { label: 'Closed Loans', value: port.closed_loans || 0, color: '#acf44e' },
                { label: 'Total Funded', value: fmtCurrency(port.total_funded), color: '#3a6ad9' },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#374151' }}>{r.label}</span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: r.color }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Funding */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Monthly Funded Volume</h3>
            {monthly.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthly} margin={{ top: 0, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1e3)}K`} />
                  <Tooltip contentStyle={tt} formatter={v => [fmtCurrency(v), 'Volume']} />
                  <Bar dataKey="volume" fill="#3a6ad9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No funded loans yet</div>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
